import { Input } from '@shared/components/ui/input';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export type PackingScanPhase = 'packing-station' | 'cart' | 'attachment-printer' | 'product';

const SCAN_PLACEHOLDERS: Record<PackingScanPhase, string> = {
  'packing-station': 'Escanea o escribe el código del puesto de packing...',
  cart: 'Escanea o escribe el código del carrito...',
  'attachment-printer': 'Escanea o escribe el código de la impresora de folios...',
  product: 'Escanea o escribe el código del producto...',
};

interface ScannerInputProps {
  scanPhase: PackingScanPhase;
  stepDescription: string;
  onScan: (code: string) => Promise<{ success: boolean; message: string }>;
  disabled?: boolean;
  disabledReason?: 'completed' | 'sending' | 'no-printer' | 'loading-cart';
}

export const ScannerInput: React.FC<ScannerInputProps> = ({
  scanPhase,
  stepDescription,
  onScan,
  disabled = false,
  disabledReason,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const inputRef = useRef<HTMLInputElement>(null);

  // Función para obtener el mensaje de deshabilitado según la razón
  const getDisabledMessage = () => {
    switch (disabledReason) {
      case 'completed':
        return 'Scanner deshabilitado - Preparación completada';
      case 'sending':
        return 'Scanner deshabilitado - Enviando datos del pedido';
      case 'no-printer':
        return 'Scanner deshabilitado - Selecciona una impresora primero';
      case 'loading-cart':
        return 'Cargando carrito, espere...';
      default:
        return 'Scanner deshabilitado';
    }
  };

  const handleSubmit = useCallback(
    async (code: string) => {
      if (!code.trim() || isProcessing || disabled) return;

      setIsProcessing(true);
      setMessage(null);

      try {
        const result = await onScan(code.trim());

        setMessage(result.message);
        setMessageType(result.success ? 'success' : 'error');

        // Limpiar input siempre (tanto en éxito como en error)
        setInputValue('');

        // Enfocar el input para el siguiente escaneo
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } catch {
        setMessage('Error al procesar el código');
        setMessageType('error');
        // Limpiar input también en caso de excepción
        setInputValue('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } finally {
        setIsProcessing(false);
      }
    },
    [onScan, isProcessing, disabled],
  );

  // Auto-focus inicial
  useEffect(() => {
    if (disabled) return;

    // Solo focus inicial al cargar el componente
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled, scanPhase]);

  // Capturar teclado globalmente
  useEffect(() => {
    if (disabled || isProcessing) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const currentHasFocus = inputRef.current === document.activeElement;

      // Permitir captura incluso sin focus, pero con ciertas excepciones
      const activeElement = document.activeElement;
      const isInModal = activeElement?.closest('[role="dialog"], .modal, .popup');
      const isTypingInImportantInput =
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
        activeElement !== inputRef.current &&
        (activeElement.getAttribute('type') === 'password' ||
          activeElement.getAttribute('type') === 'email' ||
          activeElement.getAttribute('type') === 'search' ||
          activeElement.getAttribute('placeholder')?.toLowerCase().includes('buscar') ||
          activeElement.getAttribute('placeholder')?.toLowerCase().includes('search') ||
          activeElement.closest('form') ||
          isInModal);

      // Solo ignorar si realmente se está escribiendo en un input importante
      if (isTypingInImportantInput) {
        return;
      }

      // Permitir letras con Shift (códigos de barras suelen usar mayúsculas)
      // Solo ignorar si hay modificadores Y NO es una letra/número válido
      if (
        e.ctrlKey ||
        e.metaKey ||
        e.altKey ||
        (e.shiftKey && !(e.key.length === 1 && /[a-zA-Z0-9\-_.\s/\\@#$%&*+=()[\]{}|:;"'<>,?!~`]/.test(e.key)))
      ) {
        return;
      }

      // Capturar Enter - Envío inmediato
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit(inputValue);
        return;
      }

      // Capturar Escape para limpiar
      if (e.key === 'Escape') {
        e.preventDefault();
        setInputValue('');
        return;
      }

      // Capturar caracteres válidos para códigos de barras
      if (e.key.length === 1 && /[a-zA-Z0-9\-_.\s/\\@#$%&*+=()[\]{}|:;"'<>,?!~`]/.test(e.key)) {
        e.preventDefault();

        // Si no tenemos focus, enfocar primero
        if (!currentHasFocus && inputRef.current) {
          inputRef.current.focus();
        }

        // Agregar el carácter al valor actual
        setInputValue((prev) => prev + e.key);

        // Limpiar mensaje si existe
        if (message) {
          setMessage(null);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [disabled, isProcessing, inputValue, message, handleSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (message) {
      setMessage(null); // Limpiar mensaje al escribir
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(inputValue);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(inputValue);
  };

  return (
    <div className="bg-background border border-border rounded-lg p-6">
      <div className="space-y-4">
        {/* Título y descripción */}
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">Escáner de Productos</h2>
          <p className="text-sm text-muted-foreground">{stepDescription}</p>
        </div>

        {/* Formulario de scanner */}
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder={SCAN_PLACEHOLDERS[scanPhase]}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              disabled={disabled || isProcessing}
              className="w-full text-center text-lg py-3"
              autoComplete="off"
              autoFocus
            />

            {/* Indicador de procesando */}
            {isProcessing && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              </div>
            )}
          </div>

          {/* Botón de enviar */}
          <button
            type="submit"
            disabled={!inputValue.trim() || disabled || isProcessing}
            className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
          >
            {isProcessing ? 'Procesando...' : 'Confirmar'}
          </button>
        </form>

        {/* Mensaje de resultado */}
        {message && (
          <div
            className={`p-3 rounded-lg text-center text-sm ${
              messageType === 'success'
                ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}
          >
            {message}
          </div>
        )}

        {/* Estado deshabilitado */}
        {disabled && (
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">{getDisabledMessage()}</p>
          </div>
        )}
      </div>
    </div>
  );
};
